import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Apple, Leaf, AlertCircle, CheckCircle2, XCircle, Search, RefreshCw, Utensils, List, ChefHat } from 'lucide-react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { wellnessAPI } from '../lib/api';
import { toast } from 'sonner';

const foodCategories = ['proteins', 'vegetables', 'fruits', 'grains', 'fats', 'beverages', 'dairy', 'other'];

export default function DietPage() {
  const [diet, setDiet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [cached, setCached] = useState(false);
  const [error, setError] = useState(null);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);
  
  // Filter state for food list
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    loadDietRecommendations();
  }, []);

  const loadDietRecommendations = async (forceRefresh = false) => {
    try {
      if (forceRefresh) {
        setRefreshing(true);
        toast.info('Regenerating your diet recommendations...');
      }
      const response = await wellnessAPI.getDiet(forceRefresh);
      setDiet(response.data.diet);
      setCached(response.data.cached || false);
      if (forceRefresh) {
        toast.success('Diet recommendations updated!');
      }
    } catch (err) {
      setError('Failed to load diet recommendations');
      console.error(err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    setSearchResult(null);
    try {
      const response = await wellnessAPI.searchFood(searchQuery.trim());
      setSearchResult(response.data);
    } catch (err) {
      toast.error('Failed to search food');
      console.error(err);
    } finally {
      setSearching(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'beneficial': return 'bg-green-100 text-green-700 border-green-200';
      case 'neutral': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'avoid': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'beneficial': return <CheckCircle2 className="w-5 h-5 text-green-600" />;
      case 'neutral': return <AlertCircle className="w-5 h-5 text-amber-600" />;
      case 'avoid': return <XCircle className="w-5 h-5 text-red-600" />;
      default: return <AlertCircle className="w-5 h-5 text-gray-500" />;
    }
  };

  // Filter beneficial foods by category
  const filteredBeneficial = diet?.foods?.beneficial?.filter(food => 
    selectedCategory === 'all' || food.category?.toLowerCase() === selectedCategory
  ) || [];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-64">
          <div className="spinner mb-4" />
          <p className="text-[#4A2C2A]/60">Generating personalized recommendations...</p>
          <p className="text-xs text-[#4A2C2A]/40 mt-2">This may take a moment on first load</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto space-y-6" data-testid="diet-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl text-[#4A2C2A]">Diet Recommendations</h1>
            <p className="text-sm text-[#4A2C2A]/60">Personalized nutrition guidance based on your profile</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => loadDietRecommendations(true)}
            disabled={refreshing}
            className="gap-2"
            data-testid="refresh-diet-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>

        {/* Quick Food Search */}
        <div className="soft-card p-4">
          <form onSubmit={handleSearch} className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4A2C2A]/40" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search any food to check if it's beneficial for you..."
                className="pl-10 input-warm"
                data-testid="food-search-input"
              />
            </div>
            <Button 
              type="submit" 
              disabled={searching || !searchQuery.trim()}
              className="bg-[#4A2C2A] text-white hover:bg-[#382120]"
              data-testid="food-search-btn"
            >
              {searching ? 'Checking...' : 'Check Food'}
            </Button>
          </form>
          
          {/* Search Result */}
          {searchResult && (
            <div className={`mt-4 p-4 rounded-xl border ${getStatusColor(searchResult.status)}`}>
              <div className="flex items-start gap-3">
                {getStatusIcon(searchResult.status)}
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium capitalize">{searchResult.food}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(searchResult.status)}`}>
                      {searchResult.status}
                    </span>
                    {searchResult.allergy_warning && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-red-500 text-white">Allergy Warning</span>
                    )}
                    {searchResult.restriction_conflict && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-orange-500 text-white">Restriction Conflict</span>
                    )}
                  </div>
                  <p className="text-sm mt-1 opacity-80">{searchResult.explanation}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-3 bg-[#FDFBF7] p-1 rounded-xl">
            <TabsTrigger 
              value="overview" 
              className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg gap-2"
              data-testid="tab-overview"
            >
              <Leaf className="w-4 h-4" /> Overview
            </TabsTrigger>
            <TabsTrigger 
              value="foods" 
              className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg gap-2"
              data-testid="tab-foods"
            >
              <List className="w-4 h-4" /> Food List
            </TabsTrigger>
            <TabsTrigger 
              value="meals" 
              className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg gap-2"
              data-testid="tab-meals"
            >
              <ChefHat className="w-4 h-4" /> Meal Ideas
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-6 space-y-6">
            {/* Summary Card */}
            <div className="soft-card p-6 bg-gradient-to-br from-[#8DA399]/10 to-[#FDFBF7]">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-[#8DA399]/20 flex items-center justify-center">
                  <Apple className="w-6 h-6 text-[#8DA399]" />
                </div>
                <div>
                  <h2 className="font-heading text-xl text-[#4A2C2A]">Your Diet Overview</h2>
                  {cached && <span className="text-xs text-[#4A2C2A]/40">Cached • Profile unchanged</span>}
                </div>
              </div>
              <p className="text-[#4A2C2A]/80 leading-relaxed">
                {diet?.overview?.summary || diet?.summary || 'No summary available.'}
              </p>
            </div>

            {/* Key Principles */}
            {diet?.overview?.key_principles?.length > 0 && (
              <div className="soft-card p-6">
                <h3 className="font-heading text-lg text-[#4A2C2A] mb-4">Key Principles</h3>
                <div className="grid sm:grid-cols-3 gap-4">
                  {diet.overview.key_principles.map((principle, idx) => (
                    <div key={idx} className="p-4 bg-[#FDFBF7] rounded-xl border border-[#E8A095]/10">
                      <div className="w-8 h-8 rounded-lg bg-[#E8A095]/20 flex items-center justify-center mb-3">
                        <span className="text-sm font-medium text-[#E8A095]">{idx + 1}</span>
                      </div>
                      <p className="text-sm text-[#4A2C2A]/80">{principle}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Daily Goals */}
            {diet?.overview?.daily_goals?.length > 0 && (
              <div className="soft-card p-6">
                <h3 className="font-heading text-lg text-[#4A2C2A] mb-4">Daily Goals</h3>
                <ul className="space-y-3">
                  {diet.overview.daily_goals.map((goal, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#8DA399] mt-0.5 flex-shrink-0" />
                      <span className="text-[#4A2C2A]/80">{goal}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="soft-card p-4 text-center">
                <div className="text-2xl font-heading text-[#8DA399]">
                  {diet?.foods?.beneficial?.length || 0}
                </div>
                <div className="text-sm text-[#4A2C2A]/60">Beneficial Foods</div>
              </div>
              <div className="soft-card p-4 text-center">
                <div className="text-2xl font-heading text-amber-600">
                  {diet?.foods?.neutral?.length || 0}
                </div>
                <div className="text-sm text-[#4A2C2A]/60">Neutral Foods</div>
              </div>
              <div className="soft-card p-4 text-center">
                <div className="text-2xl font-heading text-red-500">
                  {diet?.foods?.avoid?.length || 0}
                </div>
                <div className="text-sm text-[#4A2C2A]/60">Foods to Avoid</div>
              </div>
            </div>
          </TabsContent>

          {/* Food List Tab */}
          <TabsContent value="foods" className="mt-6 space-y-6">
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              <Button
                variant={selectedCategory === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory('all')}
                className={selectedCategory === 'all' ? 'bg-[#4A2C2A] text-white' : ''}
              >
                All
              </Button>
              {foodCategories.map(cat => (
                <Button
                  key={cat}
                  variant={selectedCategory === cat ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(cat)}
                  className={selectedCategory === cat ? 'bg-[#4A2C2A] text-white' : ''}
                >
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </Button>
              ))}
            </div>

            {/* Beneficial Foods */}
            <div className="soft-card p-6">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle2 className="w-5 h-5 text-[#8DA399]" />
                <h3 className="font-heading text-lg text-[#4A2C2A]">Beneficial Foods</h3>
                <span className="text-sm text-[#4A2C2A]/50">({filteredBeneficial.length})</span>
              </div>
              {filteredBeneficial.length > 0 ? (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {filteredBeneficial.map((food, idx) => (
                    <div key={idx} className="p-3 bg-green-50 border border-green-100 rounded-xl">
                      <div className="flex items-start justify-between">
                        <div>
                          <span className="font-medium text-[#4A2C2A]">{food.name}</span>
                          <span className="text-xs text-[#4A2C2A]/40 ml-2 capitalize">{food.category}</span>
                        </div>
                      </div>
                      {food.reason && (
                        <p className="text-xs text-[#4A2C2A]/60 mt-1">{food.reason}</p>
                      )}
                      {food.serving && (
                        <p className="text-xs text-[#8DA399] mt-1">Serving: {food.serving}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#4A2C2A]/50 text-sm">No foods in this category</p>
              )}
            </div>

            {/* Foods to Avoid */}
            {diet?.foods?.avoid?.length > 0 && (
              <div className="soft-card p-6">
                <div className="flex items-center gap-2 mb-4">
                  <XCircle className="w-5 h-5 text-red-500" />
                  <h3 className="font-heading text-lg text-[#4A2C2A]">Foods to Avoid</h3>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {diet.foods.avoid.map((food, idx) => (
                    <div key={idx} className="p-3 bg-red-50 border border-red-100 rounded-xl">
                      <span className="font-medium text-[#4A2C2A]">{food.name}</span>
                      {food.reason && (
                        <p className="text-xs text-[#4A2C2A]/60 mt-1">{food.reason}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Neutral Foods */}
            {diet?.foods?.neutral?.length > 0 && (
              <div className="soft-card p-6">
                <div className="flex items-center gap-2 mb-4">
                  <AlertCircle className="w-5 h-5 text-amber-500" />
                  <h3 className="font-heading text-lg text-[#4A2C2A]">Neutral / Occasional</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {diet.foods.neutral.map((food, idx) => (
                    <span key={idx} className="px-3 py-1.5 bg-amber-50 border border-amber-100 rounded-full text-sm text-[#4A2C2A]">
                      {food.name || food}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Meal Ideas Tab */}
          <TabsContent value="meals" className="mt-6 space-y-6">
            {/* Breakfast */}
            {diet?.meal_ideas?.breakfast?.length > 0 && (
              <div className="soft-card p-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">🌅</span>
                  <h3 className="font-heading text-lg text-[#4A2C2A]">Breakfast Ideas</h3>
                </div>
                <ul className="space-y-3">
                  {diet.meal_ideas.breakfast.map((idea, idx) => (
                    <li key={idx} className="flex items-start gap-3 p-3 bg-[#FDFBF7] rounded-xl">
                      <span className="text-[#E8A095] font-medium">{idx + 1}.</span>
                      <span className="text-[#4A2C2A]/80">{idea}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Lunch */}
            {diet?.meal_ideas?.lunch?.length > 0 && (
              <div className="soft-card p-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">☀️</span>
                  <h3 className="font-heading text-lg text-[#4A2C2A]">Lunch Ideas</h3>
                </div>
                <ul className="space-y-3">
                  {diet.meal_ideas.lunch.map((idea, idx) => (
                    <li key={idx} className="flex items-start gap-3 p-3 bg-[#FDFBF7] rounded-xl">
                      <span className="text-[#8DA399] font-medium">{idx + 1}.</span>
                      <span className="text-[#4A2C2A]/80">{idea}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Dinner */}
            {diet?.meal_ideas?.dinner?.length > 0 && (
              <div className="soft-card p-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">🌙</span>
                  <h3 className="font-heading text-lg text-[#4A2C2A]">Dinner Ideas</h3>
                </div>
                <ul className="space-y-3">
                  {diet.meal_ideas.dinner.map((idea, idx) => (
                    <li key={idx} className="flex items-start gap-3 p-3 bg-[#FDFBF7] rounded-xl">
                      <span className="text-[#4A2C2A]/60 font-medium">{idx + 1}.</span>
                      <span className="text-[#4A2C2A]/80">{idea}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Snacks */}
            {diet?.meal_ideas?.snacks?.length > 0 && (
              <div className="soft-card p-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">🍎</span>
                  <h3 className="font-heading text-lg text-[#4A2C2A]">Healthy Snacks</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {diet.meal_ideas.snacks.map((snack, idx) => (
                    <span key={idx} className="px-4 py-2 bg-[#E8A095]/10 border border-[#E8A095]/20 rounded-full text-sm text-[#4A2C2A]">
                      {snack}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* No meal ideas fallback */}
            {(!diet?.meal_ideas?.breakfast?.length && !diet?.meal_ideas?.lunch?.length && 
              !diet?.meal_ideas?.dinner?.length && !diet?.meal_ideas?.snacks?.length) && (
              <div className="soft-card p-8 text-center">
                <ChefHat className="w-12 h-12 mx-auto text-[#4A2C2A]/30 mb-4" />
                <p className="text-[#4A2C2A]/60">No meal ideas available yet.</p>
                <p className="text-sm text-[#4A2C2A]/40 mt-1">Chat with your wellness advisor for personalized meal suggestions.</p>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Chat CTA */}
        <div className="soft-card p-6 bg-gradient-to-r from-[#E8A095]/10 to-[#8DA399]/10">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-[#4A2C2A]">Need more guidance?</h3>
              <p className="text-sm text-[#4A2C2A]/60">Chat with your wellness advisor for personalized advice.</p>
            </div>
            <Link to="/chat">
              <Button className="bg-[#4A2C2A] text-white hover:bg-[#382120]">
                Chat Now
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
